/**
 * Country Instability Index (CII)
 * ================================
 * Computes a 0-100 risk score for each monitored country based on four
 * weighted components:
 *
 *   1. Baseline Risk (40%) — static geopolitical risk from TIER1_COUNTRIES config.
 *   2. Unrest Component (20%) — protest/conflict event frequency with fatality boost.
 *   3. Security Component (20%) — military event count, foreign military weight, GPS jamming.
 *   4. Information Velocity (20%) — severity-weighted news mention frequency.
 *
 * Post-processing:
 *   - Conflict zone floors enforce minimum scores for active war zones.
 *   - Trend is determined by comparing current score against a 1-hour-ago cache.
 *   - Final score is clamped to [0, 100].
 */

import type { GeoEvent, NewsItem, CountryRisk, ThreatLevel, EventType } from '@/types';
import {
  TIER1_COUNTRIES,
  DEFAULT_BASELINE_RISK,
  CONFLICT_ZONE_FLOORS,
} from '@/config/countries';
import { classifyThreat, severityRank } from '@/algorithms/threat-classifier';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Component weights (must sum to 1.0). */
const WEIGHT_BASELINE = 0.40;
const WEIGHT_UNREST = 0.20;
const WEIGHT_SECURITY = 0.20;
const WEIGHT_INFO_VELOCITY = 0.20;

/** Maximum raw score for each component before weighting (normalized to 100). */
const MAX_COMPONENT_SCORE = 100;

/** Trend detection threshold in absolute score points. */
const TREND_THRESHOLD = 3;

/**
 * Countries classified as "democracies" for the unrest scaling model.
 * Democracies use log-scale for protest counts (protests are routine),
 * while non-democracies use linear scaling (protests signal instability).
 */
const DEMOCRATIC_COUNTRIES = new Set([
  'US', 'GB', 'DE', 'FR', 'JP', 'KR', 'BR', 'IN', 'PH',
]);

/** Severity weights for news items used in information velocity. */
const NEWS_SEVERITY_WEIGHT: Record<ThreatLevel, number> = {
  critical: 5.0,
  high: 3.0,
  medium: 2.0,
  low: 1.0,
  info: 0.5,
};

/** How far back (ms) events are considered for scoring. */
const EVENT_WINDOW_MS = 24 * 60 * 60 * 1000; // 24 hours

/** How far back (ms) to look for the cached score to compute trend. */
const TREND_WINDOW_MS = 60 * 60 * 1000; // 1 hour

// ---------------------------------------------------------------------------
// Score cache for trend detection
// ---------------------------------------------------------------------------

interface CachedScore {
  score: number;
  timestamp: number;
}

/**
 * In-memory cache of previous CII scores keyed by country code.
 * Used to compute the escalation/de-escalation trend.
 */
const scoreCache = new Map<string, CachedScore>();

/**
 * Update the score cache. Stores the current score so the next computation
 * can compare against it for trend detection.
 */
function updateCache(countryCode: string, score: number): void {
  scoreCache.set(countryCode, { score, timestamp: Date.now() });
}

/**
 * Retrieve the cached score from approximately 1 hour ago.
 * Returns undefined if no cached score exists or if it is too old.
 */
function getCachedScore(countryCode: string): number | undefined {
  const cached = scoreCache.get(countryCode);
  if (!cached) return undefined;

  const age = Date.now() - cached.timestamp;
  // Accept scores from the last 2 hours (generous window)
  if (age > TREND_WINDOW_MS * 2) return undefined;

  return cached.score;
}

// ---------------------------------------------------------------------------
// Helper: filter events by country and time window
// ---------------------------------------------------------------------------

function filterEventsByCountry(
  events: GeoEvent[],
  countryCode: string,
  windowMs: number = EVENT_WINDOW_MS,
): GeoEvent[] {
  const cutoff = Date.now() - windowMs;
  return events.filter((e) => {
    if (e.countryCode !== countryCode) return false;
    const ts = new Date(e.timestamp).getTime();
    return ts >= cutoff;
  });
}

function filterNewsByCountry(
  news: NewsItem[],
  countryCode: string,
  countryName: string,
  windowMs: number = EVENT_WINDOW_MS,
): NewsItem[] {
  const cutoff = Date.now() - windowMs;
  const nameLower = countryName.toLowerCase();

  return news.filter((n) => {
    // Match by explicit country code
    if (n.countryCode === countryCode) {
      const ts = new Date(n.publishedAt).getTime();
      return ts >= cutoff;
    }
    // Fallback: check if country name appears in title or description
    const text = `${n.title} ${n.description ?? ''}`.toLowerCase();
    if (text.includes(nameLower)) {
      const ts = new Date(n.publishedAt).getTime();
      return ts >= cutoff;
    }
    return false;
  });
}

// ---------------------------------------------------------------------------
// Component 1: Baseline Risk
// ---------------------------------------------------------------------------

/**
 * Baseline risk from the static country configuration.
 * Returns the configured baselineRisk or the default (15) for unknown countries.
 */
function computeBaseline(countryCode: string): number {
  const config = TIER1_COUNTRIES[countryCode];
  return config?.baselineRisk ?? DEFAULT_BASELINE_RISK;
}

// ---------------------------------------------------------------------------
// Component 2: Unrest
// ---------------------------------------------------------------------------

/** Event types that contribute to the unrest component. */
const UNREST_EVENT_TYPES: Set<EventType> = new Set([
  'protest', 'conflict',
]);

/** Keywords that indicate fatalities — triggers a boost. */
const FATALITY_KEYWORDS = [
  'killed', 'dead', 'death', 'fatalities', 'casualties', 'massacre',
];

/**
 * Compute the unrest component score (0-100).
 *
 * - Counts protest/conflict events in the country within the time window.
 * - Uses log-scale for democracies (protests are normal) and linear for others.
 * - Applies a fatality boost (+15) if any event text contains fatality keywords.
 */
function computeUnrest(countryCode: string, events: GeoEvent[]): number {
  const countryEvents = events.filter((e) => UNREST_EVENT_TYPES.has(e.type));
  const count = countryEvents.length;

  if (count === 0) return 0;

  const isDemocracy = DEMOCRATIC_COUNTRIES.has(countryCode);
  const multiplier = TIER1_COUNTRIES[countryCode]?.eventMultiplier ?? 1.0;

  // Base score from event count
  let score: number;
  if (isDemocracy) {
    // Log-scale: 1 event ≈ 8, 5 events ≈ 18, 20 events ≈ 30
    score = Math.log2(count + 1) * 10;
  } else {
    // Linear-scale: each event contributes directly
    score = count * 8;
  }

  // Apply country-specific multiplier
  score *= multiplier;

  // Fatality boost: check if any event mentions fatalities
  const hasFatalities = countryEvents.some((e) => {
    const text = `${e.title} ${e.description ?? ''}`.toLowerCase();
    return FATALITY_KEYWORDS.some((kw) => text.includes(kw));
  });
  if (hasFatalities) {
    score += 15;
  }

  return Math.min(MAX_COMPONENT_SCORE, Math.round(score));
}

// ---------------------------------------------------------------------------
// Component 3: Security
// ---------------------------------------------------------------------------

/** Event types that contribute to the security component. */
const SECURITY_EVENT_TYPES: Set<EventType> = new Set([
  'military', 'terrorism', 'cyber',
]);

/** Keywords indicating foreign military involvement — double weight. */
const FOREIGN_MILITARY_KEYWORDS = [
  'foreign troops', 'nato', 'coalition', 'peacekeeping',
  'international force', 'intervention', 'allied',
];

/** GPS jamming keyword boost. */
const GPS_JAMMING_KEYWORDS = ['gps jamming', 'gps spoofing', 'navigation disruption'];

/**
 * Compute the security component score (0-100).
 *
 * - Counts military/terrorism/cyber events.
 * - Doubles weight for events involving foreign military keywords.
 * - Applies GPS jamming boost (+10).
 */
function computeSecurity(countryCode: string, events: GeoEvent[]): number {
  const securityEvents = events.filter((e) => SECURITY_EVENT_TYPES.has(e.type));

  if (securityEvents.length === 0) return 0;

  const multiplier = TIER1_COUNTRIES[countryCode]?.eventMultiplier ?? 1.0;
  let score = 0;

  for (const event of securityEvents) {
    const text = `${event.title} ${event.description ?? ''}`.toLowerCase();

    // Foreign military events are weighted 2x
    const isForeignMilitary = FOREIGN_MILITARY_KEYWORDS.some((kw) =>
      text.includes(kw),
    );
    score += isForeignMilitary ? 16 : 8;

    // GPS jamming boost
    const isGpsJamming = GPS_JAMMING_KEYWORDS.some((kw) => text.includes(kw));
    if (isGpsJamming) {
      score += 10;
    }
  }

  score *= multiplier;
  return Math.min(MAX_COMPONENT_SCORE, Math.round(score));
}

// ---------------------------------------------------------------------------
// Component 4: Information Velocity
// ---------------------------------------------------------------------------

/**
 * Compute the information velocity component score (0-100).
 *
 * Measures how intensely a country is being mentioned in news, weighted by
 * the severity of each mention.
 *
 * - Each news item contributes its severity weight.
 * - For high-volume countries (democracies with >20 mentions), log-scale is
 *   applied to avoid noise from routine coverage.
 */
function computeInfoVelocity(
  countryCode: string,
  newsItems: NewsItem[],
): number {
  if (newsItems.length === 0) return 0;

  const multiplier = TIER1_COUNTRIES[countryCode]?.eventMultiplier ?? 1.0;

  // Accumulate severity-weighted mention score
  let rawScore = 0;
  for (const item of newsItems) {
    // Use explicit severity if available; otherwise classify the title
    const severity: ThreatLevel =
      item.severity ?? classifyThreat(item.title).level;
    rawScore += NEWS_SEVERITY_WEIGHT[severity];
  }

  // For high-volume countries, apply log-scale to tame noise
  const isHighVolume = DEMOCRATIC_COUNTRIES.has(countryCode) && newsItems.length > 20;
  let score: number;
  if (isHighVolume) {
    score = Math.log2(rawScore + 1) * 8;
  } else {
    score = rawScore * 2;
  }

  score *= multiplier;
  return Math.min(MAX_COMPONENT_SCORE, Math.round(score));
}

// ---------------------------------------------------------------------------
// Trend detection
// ---------------------------------------------------------------------------

type TrendDirection = 'escalating' | 'stable' | 'de-escalating';

/**
 * Determine trend by comparing current score to the cached historical score.
 *   delta >=  3 → escalating
 *   delta <= -3 → de-escalating
 *   else        → stable
 */
function computeTrend(countryCode: string, currentScore: number): TrendDirection {
  const previous = getCachedScore(countryCode);
  if (previous === undefined) return 'stable';

  const delta = currentScore - previous;
  if (delta >= TREND_THRESHOLD) return 'escalating';
  if (delta <= -TREND_THRESHOLD) return 'de-escalating';
  return 'stable';
}

// ---------------------------------------------------------------------------
// Top threats extraction
// ---------------------------------------------------------------------------

/**
 * Extract the top 5 threat descriptions from country events and news.
 * Uses the threat classifier to rank items by severity, then returns
 * the highest-severity items' titles.
 */
function extractTopThreats(events: GeoEvent[], news: NewsItem[]): string[] {
  const items: { text: string; rank: number }[] = [];

  for (const e of events) {
    items.push({ text: e.title, rank: severityRank(e.severity) });
  }
  for (const n of news) {
    const sev = n.severity ?? classifyThreat(n.title).level;
    items.push({ text: n.title, rank: severityRank(sev) });
  }

  // Sort by severity descending, take top 5, deduplicate
  items.sort((a, b) => b.rank - a.rank);

  const seen = new Set<string>();
  const threats: string[] = [];
  for (const item of items) {
    const normalized = item.text.trim();
    if (normalized && !seen.has(normalized)) {
      seen.add(normalized);
      threats.push(normalized);
      if (threats.length >= 5) break;
    }
  }

  return threats;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Compute the Country Instability Index for a single country.
 *
 * @param countryCode - ISO 3166-1 alpha-2 country code (e.g., 'US', 'UA').
 * @param events - All currently tracked GeoEvents (will be filtered internally).
 * @param news - All currently tracked NewsItems (will be filtered internally).
 * @returns CountryRisk object with score, components, trend, and top threats.
 *
 * @example
 * ```ts
 * const risk = computeCII('UA', allEvents, allNews);
 * // { code: 'UA', name: 'Ukraine', score: 78, trend: 'escalating', ... }
 * ```
 */
export function computeCII(
  countryCode: string,
  events: GeoEvent[],
  news: NewsItem[],
): CountryRisk {
  const config = TIER1_COUNTRIES[countryCode];
  const name = config?.name ?? countryCode;
  const nameKo = config?.nameKo;

  // Filter events and news for this country
  const countryEvents = filterEventsByCountry(events, countryCode);
  const countryNews = filterNewsByCountry(news, countryCode, name);

  // Compute individual components (each 0-100)
  const baseline = computeBaseline(countryCode);
  const unrest = computeUnrest(countryCode, countryEvents);
  const security = computeSecurity(countryCode, countryEvents);
  const infoVelocity = computeInfoVelocity(countryCode, countryNews);

  // Weighted composite score
  let score = Math.round(
    baseline * WEIGHT_BASELINE +
    unrest * WEIGHT_UNREST +
    security * WEIGHT_SECURITY +
    infoVelocity * WEIGHT_INFO_VELOCITY,
  );

  // Apply conflict zone floors
  const floor = CONFLICT_ZONE_FLOORS[countryCode];
  if (floor !== undefined && score < floor) {
    score = floor;
  }

  // Clamp to [0, 100]
  score = Math.max(0, Math.min(100, score));

  // Trend detection
  const trend = computeTrend(countryCode, score);

  // Update cache for next computation cycle
  updateCache(countryCode, score);

  // Extract top threats
  const topThreats = extractTopThreats(countryEvents, countryNews);

  return {
    code: countryCode,
    name,
    ...(nameKo ? { nameKo } : {}),
    score,
    trend,
    components: {
      baseline,
      unrest,
      security,
      infoVelocity,
    },
    eventCount: countryEvents.length,
    topThreats,
  };
}

/**
 * Compute CII for all Tier-1 countries plus any additional country that
 * appears in the current events dataset.
 *
 * @param events - All currently tracked GeoEvents.
 * @param news - All currently tracked NewsItems.
 * @returns Array of CountryRisk objects, sorted by score descending.
 *
 * @example
 * ```ts
 * const allRisks = computeAllCII(events, news);
 * // Returns risks for all 22 Tier-1 countries + any extra countries from events
 * ```
 */
export function computeAllCII(
  events: GeoEvent[],
  news: NewsItem[],
): CountryRisk[] {
  // Collect all country codes: Tier-1 + any appearing in events
  const countryCodes = new Set<string>(Object.keys(TIER1_COUNTRIES));

  for (const event of events) {
    if (event.countryCode) {
      countryCodes.add(event.countryCode);
    }
  }

  // Compute CII for each country
  const results: CountryRisk[] = [];
  for (const code of countryCodes) {
    results.push(computeCII(code, events, news));
  }

  // Sort by score descending (highest risk first)
  results.sort((a, b) => b.score - a.score);

  return results;
}

/**
 * Reset the internal score cache. Useful for testing or when
 * re-initializing the system.
 */
export function resetScoreCache(): void {
  scoreCache.clear();
}
